import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';
import produce from 'immer';

import * as API from '../api/index.js';
import type {
  DataFilesV1QueryResult,
  PastImportsSearchParams,
} from '../api/types.js';
import { getPaginatedDataPlaceholder } from '../constants/tablesConfig.js';
import { useImportHubV2 } from '../hooks/ff/index.js';
import { useEnhancedMutation, useTypedQuery } from '../hooks/index.js';
import { QueryKeys } from './queryClient.js';

export const useDataFilesListQueryOptions = () => {
  const isImportHubV2 = useImportHubV2();

  return (
    isImportHubV2
      ? DataIngestion.DataFile.listDataFilesV2QueryOptions
      : DataIngestion.DataFile.listDataFilesV1QueryOptions
  ) as typeof DataIngestion.DataFile.listDataFilesV1QueryOptions;
};

export const useDataFilesFilterOptionsQueryOptions = () => {
  const isImportHubV2 = useImportHubV2();

  return (
    isImportHubV2
      ? DataIngestion.DataFile.getDataFileFilterOptionsV2QueryOptions
      : DataIngestion.DataFile.getDataFileFilterOptionsV1QueryOptions
  ) as typeof DataIngestion.DataFile.getDataFileFilterOptionsV1QueryOptions;
};

export const useTableDataFiles = (options?: { throwOnError?: boolean }) => {
  const getQueryOptions = useDataFilesListQueryOptions();

  const queryOptions = getQueryOptions({
    params: {},
  });

  const query = useTypedQuery({
    ...queryOptions,
    refetchOnMount: false,
    placeholderData: getPaginatedDataPlaceholder(),
    staleTime: 30 * 1000,
    ...options,
  });

  return query;
};

export const useTotalFilesToArchiveCount = () => {
  return useTypedQuery({
    queryKey: QueryKeys.dataFiles.totalFilesToArchiveCount(),
    queryFn: () => API.getTotalFilesToArchiveCount(),
  });
};

export const useDeleteTableDataFileMutation = (
  searchParams: PastImportsSearchParams,
) => {
  const queryClient = useQueryClient();
  const dataFilesQueryKey = DataIngestion.DataFile.listDataFilesV1QueryOptions({
    params: {
      ...searchParams,
    },
  }).queryKey;

  return useEnhancedMutation({
    mutationFn: ({ id, meta }: { id: string; meta: { index: number } }) =>
      API.deleteTableDataFile(id),
    onMutate: (vars) => {
      queryClient.setQueryData<DataFilesV1QueryResult>(
        dataFilesQueryKey,
        (old) => {
          return produce(old, (draft) => {
            if (!draft) {
              return;
            }

            const dataFileToDelete = draft.items[vars.meta.index];
            if (!dataFileToDelete) {
              return;
            }

            if (dataFileToDelete.status === 'uploaded') {
              dataFileToDelete.status = 'deleting';
            } else {
              draft.items.splice(vars.meta.index, 1);
            }
          });
        },
      );
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to delete import'),
        };
      },
    },
  });
};

export const useDeleteDataFile = () => {
  const queryClient = useQueryClient();

  return useEnhancedMutation({
    mutationFn: ({ id }: { id: string }) => API.deleteTableDataFile(id),
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['dataFilesList'],
      });
    },
    meta: {
      getErrorsMap(t) {
        return {
          default: t('Failed to delete import'),
        };
      },
    },
  });
};
